import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "filterSize",
    "filterColour",
    "productCard",
    "noProductMessage",
    "selectedSizesContainer",
    "selectedColoursContainer",
    "sizeCheckbox",
    "colourCheckbox",
    "colourDisabledOverlay"
  ];

  filterSizes = [];
  filterColours = [];
  numberOfProducts = 0;

  connect() {
    console.log("Product Filter Controller Connected");
  }

  filterBySize(e) {
    const checkbox = e.target;
    if (checkbox.checked) {
      this.filterSizes.push(checkbox.value);
    } else {
      this.filterSizes = this.filterSizes.filter(v => v !== checkbox.value);
    }

    this.renderSelectedFilters();
    this.updateAvailableOptions();
    this.filterProducts();
  }

  filterByColour(e) {
    const checkbox = e.target;
    if (checkbox.checked) {
      // Store both name and hex
      this.filterColours.push({
        name: checkbox.value,
        hex: checkbox.dataset.hex || "#000000"
      });
    } else {
      this.filterColours = this.filterColours.filter(c => c.name !== checkbox.value);
    }

    this.renderSelectedFilters();
    this.updateAvailableOptions();
    this.filterProducts();
  }

  // Render selected filters in the containers
  renderSelectedFilters() {
    // Render sizes
    this.selectedSizesContainerTarget.innerHTML = "";
    this.filterSizes.forEach(size => {
      const div = document.createElement("div");
      div.textContent = size;
      div.className = "filter-size-pill-styling";
      this.selectedSizesContainerTarget.appendChild(div);
    });

    // Render colours
    this.selectedColoursContainerTarget.innerHTML = "";
    this.filterColours.forEach(c => {
      const div = document.createElement("div");
      div.style.backgroundColor = c.hex;
      div.className = "filter-colour-pill-styling";
      this.selectedColoursContainerTarget.appendChild(div);
    });
  }

  updateAvailableOptions() {
    // Get available sizes and colors based on current filters
    const availableSizes = new Set();
    const availableColours = new Set();

    this.productCardTargets.forEach(card => {
      const cardSizes = card.dataset.availableSizes
        ? card.dataset.availableSizes.split(",").map(s => parseFloat(s.trim()))
        : [];
      const cardColour = card.dataset.availableColour || "DefaultColour";

      // If sizes are filtered, check which colors are available for those sizes
      if (this.filterSizes.length > 0) {
        const matchesSize = this.filterSizes.some(size =>
          cardSizes.includes(parseFloat(size))
        );
        if (matchesSize) {
          availableColours.add(cardColour);
        }
      }

      // If colors are filtered, check which sizes are available for those colors
      if (this.filterColours.length > 0) {
        const matchesColour = this.filterColours.some(c => c.name === cardColour);
        if (matchesColour) {
          cardSizes.forEach(size => availableSizes.add(size));
        }
      }

      // If no filters are active, all options are available
      if (this.filterSizes.length === 0 && this.filterColours.length === 0) {
        availableColours.add(cardColour);
        cardSizes.forEach(size => availableSizes.add(size));
      }
    });

    // Update size checkboxes
    if (this.hasSizeCheckboxTarget) {
      this.sizeCheckboxTargets.forEach(checkbox => {
        const isAvailable = this.filterColours.length === 0 ||
                           availableSizes.has(parseFloat(checkbox.value));
        const parentDiv = checkbox.closest('.relative');

        if (!checkbox.checked) {
          checkbox.disabled = !isAvailable;
          if (parentDiv) {
            parentDiv.style.opacity = isAvailable ? '1' : '0.3';
            parentDiv.style.pointerEvents = isAvailable ? 'auto' : 'none';
          }
        }
      });
    }

    // Update colour checkboxes
    if (this.hasColourCheckboxTarget) {
      this.colourCheckboxTargets.forEach((checkbox, index) => {
        const isAvailable = this.filterSizes.length === 0 ||
                           availableColours.has(checkbox.value);
        const parentDiv = checkbox.closest('.relative');
        const overlay = this.colourDisabledOverlayTargets[index];

        if (!checkbox.checked) {
          checkbox.disabled = !isAvailable;
          if (parentDiv) {
            parentDiv.style.opacity = isAvailable ? '1' : '0.5';
            parentDiv.style.pointerEvents = isAvailable ? 'auto' : 'none';
          }
          if (overlay) {
            overlay.classList.toggle('hidden', isAvailable);
          }
        }
      });
    }
  }

  filterProducts() {
    this.numberOfProducts = 0;

    this.productCardTargets.forEach(card => {
      const cardSizes = card.dataset.availableSizes
        ? card.dataset.availableSizes.split(",").map(s => parseFloat(s.trim()))
        : [];
      const cardColour = card.dataset.availableColour || "DefaultColour";

      const matchesSize =
        this.filterSizes.length === 0 ||
        this.filterSizes.some(size => cardSizes.includes(parseFloat(size)));

      const matchesColour =
        this.filterColours.length === 0 ||
        this.filterColours.some(c => c.name === cardColour);

      const show = matchesSize && matchesColour;

      if (show) this.numberOfProducts++;
      card.style.display = show ? "block" : "none";
    });

    if (this.hasNoProductMessageTarget) {
      this.noProductMessageTarget.style.display =
        this.numberOfProducts > 0 ? "none" : "block";
    }
  }
}
